import { Link } from "@tanstack/react-router";
import { ChevronRight, Clock, UtensilsCrossed } from "lucide-react";

import MarqueeRail, { duplicateForMarquee } from "@/components/MarqueeRail";
import { useLiveStatus } from "@/components/districts/OpenStatusPill";
import { SmartImage } from "@/components/ui/SmartImage";
import { DAY_KEYS, dayOf, formatDayHours, helsinkiNow } from "@/lib/opening-hours";
import type { DistrictVenueDTO } from "@/lib/districts.functions";

const UI = '"Inter Variable", Inter, ui-sans-serif, system-ui, sans-serif';

function prettyHours(day: ReturnType<typeof dayOf>): string | null {
  if (!day) return null;
  const label = formatDayHours(day);
  if (!label || /closed/i.test(label) || label === "—") return null;
  return label.replace(/:00/g, "").replace(/\s*[-–]\s*/, "–");
}

export function lunchLabel(v: DistrictVenueDTO): string | null {
  const hours = v.lunch_hours;
  const today = prettyHours(dayOf(hours, helsinkiNow().dayKey));
  if (today) return today;
  for (const key of DAY_KEYS) {
    const label = prettyHours(dayOf(hours, key));
    if (label) return label;
  }
  return null;
}

export function hasLunch(v: DistrictVenueDTO): boolean {
  return !!lunchLabel(v);
}

function menuHref(v: DistrictVenueDTO): string | null {
  return v.menu_external_url ?? v.menu_pdf_url ?? v.menu_image_url ?? null;
}

function prettyName(s: string) {
  const t = s.trim();
  if (t === t.toUpperCase() && /[A-Z]/.test(t)) {
    return t.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return t;
}

function statusText(state: string) {
  if (state === "open") return "Open now";
  if (state === "closing_soon") return "Closing soon";
  if (state === "closed") return "Closed";
  return null;
}

function LunchCard({ v }: { v: DistrictVenueDTO }) {
  const lunch = lunchLabel(v);
  const menu = menuHref(v);
  const category = v.category ?? (v.tags ?? []).slice(0, 2).join(" · ");
  const status = useLiveStatus(v);
  const openLabel = statusText(status.state);
  const open = status.state === "open" || status.state === "closing_soon";
  const hoursLine = lunch
    ? `Lunch ${lunch}`
    : status.todayLabel && status.todayLabel !== "—"
      ? status.todayLabel
      : null;

  return (
    <article
      className="group flex w-[136px] shrink-0 flex-col overflow-hidden rounded-lg border border-navy/10 bg-white shadow-[0_8px_20px_-16px_rgba(15,27,61,0.35)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-16px_rgba(15,27,61,0.28)] sm:w-[240px] lg:w-[340px]"
      style={{ fontFamily: UI, fontStyle: "normal" }}
    >
      <Link to="/place/$id" params={{ id: v.slug }} className="flex min-h-0 flex-1 flex-col">
        <div className="relative aspect-[4/3] overflow-hidden bg-slate-100 sm:aspect-[16/10]">
          {v.image ? (
            <SmartImage
              src={v.image}
              alt={prettyName(v.name)}
              className="size-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
            />
          ) : (
            <div className="size-full bg-gradient-to-br from-navy/10 to-navy/5" />
          )}
          <div aria-hidden className="pointer-events-none absolute inset-0 hidden bg-gradient-to-t from-black/45 via-black/0 to-black/10 sm:block" />

          {openLabel ? (
            <span
              className={`absolute left-2.5 top-2.5 hidden items-center gap-1.5 rounded-full px-2 py-1 text-[10px] font-bold uppercase tracking-wide shadow-sm backdrop-blur sm:inline-flex ${
                open ? "bg-white/95 text-emerald-700" : "bg-white/95 text-navy/70"
              }`}
            >
              <span className={`size-1.5 rounded-full ${open ? "bg-emerald-500" : "bg-navy/35"} ${open ? "animate-pulse" : ""}`} />
              {openLabel}
            </span>
          ) : null}

          {status.todayLabel && status.todayLabel !== "—" ? (
            <span className="absolute bottom-2.5 left-2.5 hidden rounded-full bg-black/45 px-2 py-0.5 text-[10px] font-semibold text-white backdrop-blur-sm sm:inline">
              Today {status.todayLabel}
            </span>
          ) : lunch ? (
            <span className="absolute bottom-2.5 left-2.5 hidden rounded-full bg-black/45 px-2 py-0.5 text-[10px] font-semibold text-white backdrop-blur-sm sm:inline">
              Lunch {lunch}
            </span>
          ) : null}
        </div>

        <div className="flex flex-1 flex-col px-2.5 pb-1 pt-2 sm:px-4 sm:pb-0 sm:pt-3.5">
          <h3 className="line-clamp-1 text-[13px] font-semibold leading-snug tracking-tight text-navy sm:line-clamp-2 sm:text-[17px]">
            {prettyName(v.name)}
          </h3>
          <p className="mt-0.5 line-clamp-1 text-[11px] text-navy/50 sm:text-[12px]">{category || "\u00a0"}</p>
          {hoursLine ? (
            <p className="mt-1 flex items-center gap-1 text-[11px] font-medium text-navy/55 sm:hidden">
              <Clock className="size-3 shrink-0 text-navy/40" strokeWidth={2.25} />
              {hoursLine}
            </p>
          ) : null}
        </div>
      </Link>

      <div className="mt-auto px-2.5 pb-2.5 pt-2 sm:px-4 sm:pb-4 sm:pt-3">
        {menu ? (
          <a
            href={menu}
            target="_blank"
            rel="noreferrer"
            className="inline-flex h-7 w-full items-center justify-center gap-0.5 rounded-md bg-navy px-2.5 text-[11px] font-semibold text-white transition-colors hover:bg-navy/90 sm:h-9 sm:gap-1 sm:px-3 sm:text-[12px]"
          >
            <UtensilsCrossed className="hidden size-3.5 sm:block" strokeWidth={2.25} />
            <span className="sm:hidden">Lunch menu</span>
            <span className="hidden sm:inline">View menu</span>
            <ChevronRight className="size-3 sm:hidden" />
          </a>
        ) : (
          <Link
            to="/place/$id"
            params={{ id: v.slug }}
            className="inline-flex h-7 w-full items-center justify-center gap-0.5 rounded-md bg-navy px-2.5 text-[11px] font-semibold text-white transition-colors hover:bg-navy/90 sm:h-9 sm:border sm:border-navy/15 sm:bg-white sm:px-3 sm:text-[12px] sm:text-navy sm:hover:bg-slate-50"
          >
            <span className="sm:hidden">Lunch menu</span>
            <span className="hidden sm:inline">View place</span>
            <ChevronRight className="size-3 sm:size-3.5" />
          </Link>
        )}
      </div>
    </article>
  );
}

export function DistrictLunchRail({
  venues,
  direction = "right",
}: {
  venues: DistrictVenueDTO[];
  direction?: "left" | "right";
}) {
  if (venues.length === 0) return null;
  const loop = duplicateForMarquee(venues.slice(0, 12));

  return (
    <MarqueeRail className="!mx-0 !px-0" trackClassName="marquee-track gap-3 sm:gap-4" direction={direction}>
      {loop.map((v, i) => (
        <LunchCard key={`${v.id}-${i}`} v={v} />
      ))}
    </MarqueeRail>
  );
}
